import React, { useEffect, useMemo, useState } from 'react';
import { FileUp, MessageCircle, PenTool, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Languages } from '../../constants';
import {
    createProcess,
    loadProcessIndustries,
    pickDefaultIndustryId,
    sortProcessIndustries,
    type ProcessIndustry,
} from '../../services/api/processCreationService';
import { createBlankMapping, toLocalSheetFromCreatedMapping } from '../../services/api/mappingCreationService';
import { useAppSelector } from '../../stores';
import { navigateToMapping } from '../../utils/mappingAccess';
import { navigateToInterviewWithFallback } from '../../utils/smartNavigation';

type CreationMethod = 'interview' | 'upload' | 'draw';
/** Aligné sur ProcessCreate `/human` : HUMAN = fichier texte, IMAGE = fichier image. */
type DocumentKind = 'text' | 'image';
type OutputType = 'standard' | 'bpmn';

interface CreateDiagramModalProps {
    open: boolean;
    onClose: () => void;
    initialMethod?: CreationMethod;
}

const CreateDiagramModal = ({ open, onClose, initialMethod = 'interview' }: CreateDiagramModalProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const users = useAppSelector((state) => state.auth);
    const uiLanguage = localStorage.getItem('currentLanguage') === 'fr' ? 'french' : 'english';
    const [method, setMethod] = useState<CreationMethod>(initialMethod);
    const [outputType, setOutputType] = useState<OutputType>('standard');
    const [documentKind, setDocumentKind] = useState<DocumentKind>('text');
    const [industries, setIndustries] = useState<ProcessIndustry[]>([]);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [industryId, setIndustryId] = useState('');
    const [language, setLanguage] = useState(uiLanguage);
    const [loadingIndustries, setLoadingIndustries] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const processType =
        method === 'interview'
            ? 'AI'
            : method === 'draw'
              ? 'MANUAL'
              : documentKind === 'text'
                ? 'HUMAN'
                : 'IMAGE';

    const sortedIndustries = useMemo(() => sortProcessIndustries(industries), [industries]);

    useEffect(() => {
        if (!open) return;

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && !submitting) onClose();
        };
        window.addEventListener('keydown', onKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [onClose, open, submitting]);

    useEffect(() => {
        if (open) setMethod(initialMethod);
    }, [initialMethod, open]);

    const isDrawOnly = method === 'draw';

    useEffect(() => {
        // Canvas vierge : secteur/langue auto — pas besoin de précharger la liste.
        if (!open || isDrawOnly || industries.length > 0 || !users.access_token) return;

        const loadIndustries = async () => {
            setLoadingIndustries(true);
            setError('');
            try {
                setIndustries(await loadProcessIndustries(users.access_token, uiLanguage));
            } catch {
                setError(t('views.processView.homeCreateIndustryLoadError'));
            } finally {
                setLoadingIndustries(false);
            }
        };

        void loadIndustries();
    }, [industries.length, isDrawOnly, open, t, uiLanguage, users.access_token]);

    const resolveIndustryIdForSubmit = async (): Promise<string | null> => {
        if (industryId) return industryId;
        if (!isDrawOnly) return null;

        let list = industries;
        if (!list.length) {
            list = await loadProcessIndustries(users.access_token, uiLanguage);
            setIndustries(list);
        }
        return pickDefaultIndustryId(list);
    };

    if (!open) return null;

    const resetAndClose = () => {
        if (submitting) return;
        setError('');
        onClose();
    };

    const submit = async (event: React.FormEvent) => {
        event.preventDefault();
        const trimmedName = name.trim();
        const trimmedDescription = description.trim();

        if (!trimmedName) {
            setError(t('form.nameRequired'));
            return;
        }
        if (!isDrawOnly && !industryId) {
            setError(t('form.createProcess.industryRequired'));
            return;
        }
        if (!isDrawOnly && trimmedDescription && trimmedDescription.length < 5) {
            setError(t('form.createProcess.descriptionTooShort'));
            return;
        }

        setSubmitting(true);
        setError('');
        try {
            const resolvedIndustryId = isDrawOnly ? await resolveIndustryIdForSubmit() : industryId;
            if (!resolvedIndustryId) {
                setError(t('views.processView.homeCreateIndustryLoadError'));
                return;
            }

            const response = await createProcess(
                {
                    title: trimmedName,
                    description: isDrawOnly ? '' : trimmedDescription,
                    industryId: resolvedIndustryId,
                    language: isDrawOnly ? uiLanguage : language,
                    type: processType,
                    diagramOutput: method === 'interview' && outputType === 'bpmn' ? 'BPMN' : 'STANDARD',
                },
                users.access_token,
            );

            if (response.status !== 201 || !response.body?.data?._id) {
                if (response.body?.description === 'Process name already exist') {
                    setError(t('form.createProcess.processNameAlreadyExist'));
                } else if (response.body?.status_code === 426) {
                    setError(t('form.createProcess.processCreateLimite'));
                } else {
                    setError(t('navigation.anErrorOcured'));
                }
                return;
            }

            const processId = response.body.data._id;
            if (method === 'draw') {
                const mappingResponse = await createBlankMapping(
                    processId,
                    trimmedName,
                    users.access_token,
                );
                const mapId = mappingResponse.body?.data?._id || mappingResponse.body?.data?.id;
                if (mappingResponse.status !== 200 || !mapId) {
                    setError(t('views.processView.homeCreateBlankDiagramError'));
                    return;
                }
                const localSheet = toLocalSheetFromCreatedMapping(
                    mappingResponse.body?.data as Record<string, unknown>,
                    String(mapId),
                );
                const result = await navigateToMapping({
                    processId,
                    mapId: String(mapId),
                    accessToken: users.access_token,
                    localSheets: localSheet ? [localSheet] : undefined,
                    knownAccessible: true,
                    blankCanvasOpen: true,
                    navigate,
                });
                if (!result.success) setError(t('navigation.anErrorOcured'));
                return;
            }

            const navigated = await navigateToInterviewWithFallback(
                processId,
                users.access_token,
                navigate,
            );
            if (!navigated) setError(t('navigation.anErrorOcured'));
        } catch {
            setError(t('navigation.anErrorOcured'));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fh-modal-backdrop" role="presentation" onMouseDown={resetAndClose}>
            <div
                className="fh-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="fh-create-title"
                onMouseDown={(event) => event.stopPropagation()}
            >
                <div className="fh-modal-header">
                    <div>
                        <span className="fh-eyebrow">{t('views.processView.homeCreateEyebrow')}</span>
                        <h2 id="fh-create-title">{t('views.processView.homeBuildDiagram')}</h2>
                        <p>
                            {t(
                                isDrawOnly
                                    ? 'views.processView.homeCreateDrawSubtitle'
                                    : 'views.processView.homeCreateSubtitle',
                            )}
                        </p>
                    </div>
                    <button
                        type="button"
                        className="fh-modal-close"
                        onClick={resetAndClose}
                        aria-label={t('words.close')}
                        disabled={submitting}
                    >
                        <X size={18} aria-hidden="true" />
                    </button>
                </div>

                <form onSubmit={submit}>
                    <div className="fh-modal-content">
                        <fieldset className="fh-method-fieldset">
                            <legend>{t('views.processView.homeStartingMethod')}</legend>
                            <div className="fh-method-grid">
                                <button
                                    type="button"
                                    className={`fh-method-card${method === 'interview' ? ' selected' : ''}`}
                                    onClick={() => setMethod('interview')}
                                    aria-pressed={method === 'interview'}
                                >
                                    <span className="fh-method-icon interview"><MessageCircle size={19} /></span>
                                    <strong>{t('views.processView.homeMethodInterview')}</strong>
                                    <small>{t('views.processView.homeMethodThorough')}</small>
                                </button>
                                <button
                                    type="button"
                                    className={`fh-method-card${method === 'upload' ? ' selected' : ''}`}
                                    onClick={() => setMethod('upload')}
                                    aria-pressed={method === 'upload'}
                                >
                                    <span className="fh-method-icon upload"><FileUp size={19} /></span>
                                    <strong>{t('views.processView.homeMethodUpload')}</strong>
                                    <small>{t('views.processView.homeMethodUploadTypes')}</small>
                                </button>
                                <button
                                    type="button"
                                    className={`fh-method-card${method === 'draw' ? ' selected' : ''}`}
                                    onClick={() => setMethod('draw')}
                                    aria-pressed={method === 'draw'}
                                >
                                    <span className="fh-method-icon draw"><PenTool size={19} /></span>
                                    <strong>{t('views.processView.homeMethodDraw')}</strong>
                                    <small>{t('views.processView.homeMethodBlankCanvas')}</small>
                                </button>
                            </div>
                            <p className="fh-method-blurb">
                                {t(
                                    method === 'interview'
                                        ? 'views.processView.homeMethodInterviewBlurb'
                                        : method === 'draw'
                                          ? 'views.processView.homeMethodDrawBlurb'
                                          : documentKind === 'text'
                                            ? 'views.processView.homeMethodUploadTextBlurb'
                                            : 'views.processView.homeMethodUploadImageBlurb',
                                )}
                            </p>
                        </fieldset>

                        {method === 'interview' && (
                            <fieldset className="fh-method-fieldset">
                                <legend>{t('views.processView.homeOutputType')}</legend>
                                <div className="fh-doc-kind-grid">
                                    <button
                                        type="button"
                                        className={`fh-doc-kind${outputType === 'standard' ? ' selected' : ''}`}
                                        onClick={() => setOutputType('standard')}
                                        aria-pressed={outputType === 'standard'}
                                    >
                                        <span
                                            className={`fh-doc-radio${outputType === 'standard' ? ' on' : ''}`}
                                            aria-hidden="true"
                                        />
                                        <span>{t('views.processView.homeOutputStandard')}</span>
                                    </button>
                                    <button
                                        type="button"
                                        className={`fh-doc-kind${outputType === 'bpmn' ? ' selected' : ''}`}
                                        onClick={() => setOutputType('bpmn')}
                                        aria-pressed={outputType === 'bpmn'}
                                    >
                                        <span
                                            className={`fh-doc-radio${outputType === 'bpmn' ? ' on' : ''}`}
                                            aria-hidden="true"
                                        />
                                        <span>{t('views.processView.homeOutputBpmn')}</span>
                                    </button>
                                </div>
                                <p className="fh-method-blurb">
                                    {t(
                                        outputType === 'bpmn'
                                            ? 'views.processView.homeOutputBpmnBlurb'
                                            : 'views.processView.homeOutputStandardBlurb',
                                    )}
                                </p>
                            </fieldset>
                        )}

                        {method === 'upload' && (
                            <fieldset className="fh-method-fieldset">
                                <legend>{t('views.processView.homeDocumentType')}</legend>
                                <div className="fh-doc-kind-grid">
                                    <button
                                        type="button"
                                        className={`fh-doc-kind${documentKind === 'text' ? ' selected' : ''}`}
                                        onClick={() => setDocumentKind('text')}
                                        aria-pressed={documentKind === 'text'}
                                    >
                                        <span
                                            className={`fh-doc-radio${documentKind === 'text' ? ' on' : ''}`}
                                            aria-hidden="true"
                                        />
                                        <span>{t('form.createProcess.humanLedInterview')}</span>
                                    </button>
                                    <button
                                        type="button"
                                        className={`fh-doc-kind${documentKind === 'image' ? ' selected' : ''}`}
                                        onClick={() => setDocumentKind('image')}
                                        aria-pressed={documentKind === 'image'}
                                    >
                                        <span
                                            className={`fh-doc-radio${documentKind === 'image' ? ' on' : ''}`}
                                            aria-hidden="true"
                                        />
                                        <span>{t('form.createProcess.imageBasedMapping')}</span>
                                    </button>
                                </div>
                            </fieldset>
                        )}

                        <div className="fh-modal-divider" />

                        <label className="fh-field">
                            <span>{t('views.processView.homeProcessName')} <b>*</b></span>
                            <input
                                value={name}
                                onChange={(event) => setName(event.target.value)}
                                placeholder={t('views.processView.homeProcessNamePlaceholder')}
                                autoFocus
                                maxLength={120}
                            />
                        </label>
                        {!isDrawOnly && (
                            <>
                                <label className="fh-field">
                                    <span>{t('views.processView.homeProcessPurpose')}</span>
                                    <textarea
                                        value={description}
                                        onChange={(event) => setDescription(event.target.value)}
                                        placeholder={t('views.processView.homeProcessPurposePlaceholder')}
                                        maxLength={1000}
                                    />
                                </label>
                                <div className="fh-field-row">
                                    <label className="fh-field">
                                        <span>{t('views.processView.homeSelectIndustry')} <b>*</b></span>
                                        <select
                                            value={industryId}
                                            onChange={(event) => setIndustryId(event.target.value)}
                                            disabled={loadingIndustries}
                                        >
                                            <option value="">
                                                {loadingIndustries
                                                    ? t('words.loading')
                                                    : t('views.processView.homeSelectIndustry')}
                                            </option>
                                            {sortedIndustries.map((industry) => (
                                                <option key={industry._id} value={industry._id}>
                                                    {industry.name}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                    <label className="fh-field">
                                        <span>{t('views.processView.homeInterviewLanguage')}</span>
                                        <select
                                            value={language}
                                            onChange={(event) => setLanguage(event.target.value)}
                                        >
                                            {Languages.map((item: { label: string; value: string }) => (
                                                <option key={item.value} value={item.value}>
                                                    {item.label}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                </div>
                            </>
                        )}

                        {error && <div className="fh-form-error" role="alert">{error}</div>}
                    </div>

                    <div className="fh-modal-footer">
                        <button type="button" className="fh-cancel-btn" onClick={resetAndClose} disabled={submitting}>
                            {t('words.cancel')}
                        </button>
                        <button
                            type="submit"
                            className="fh-primary-btn"
                            disabled={submitting || (!isDrawOnly && loadingIndustries)}
                        >
                            {submitting
                                ? t('words.loading')
                                : t(
                                    method === 'interview'
                                        ? 'views.processView.homeCreateInterview'
                                        : method === 'draw'
                                          ? 'views.processView.homeCreateBlankDiagram'
                                          : documentKind === 'text'
                                            ? 'views.processView.homeCreateUploadText'
                                            : 'views.processView.homeCreateUploadImage',
                                )}
                            {!submitting && <span aria-hidden="true">→</span>}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateDiagramModal;
